import { MouseEvent, useEffect, useMemo, useState } from "react";
import { GlobalAttributeFields } from "./forms/schema";
import { useFormContext } from "react-hook-form";
import StepOne from "./components/StepOne";
import StepTwo from "./components/StepTwo";
import StepThree from "./components/StepThree";
import StepFour from "./components/StepFour";
import StepFive from "./components/StepFive.tsx";
import { useDispatch, useSelector } from "react-redux";
import { newOrderActions } from "features/reducers/newOrder/newOrder.ts";
import { Button, DialogContent } from "@mui/material";
import AddGlobalAttributesDialog from "../AddGlobalAttributesDialog";
import { AddGlobalAttributesGroupButton, NAOptionText, NewOrderUpdateGroup } from "../constants";
import { AddGlobalsButton } from "./addGlobalAttributes.styles";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { RootState } from "stores/application.store";
import { AddItemText } from "constants/text";
import { useNavigate, useParams } from "react-router";
import { models } from "types/api/viewModels.ts";
import { v4 as uuidv4 } from "uuid";
import { useLazyGetFinishDataQuery, useLazyGetSpeciesDataQuery } from "features/api/globalsApi.ts";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import { OrderType } from "types/declarations/enums.ts";
import { useDialog } from "hooks/useDialog.ts";
import FixGlobalAttributesDialog from "../FixGlobalAttributesDialog/FixGlobalAttributesDialog.tsx";
import { fileNameSymbol } from "../CSVUpload/CSVUpload";
import { useUserLocation } from "hooks/useUserLocation.ts";

const stepKeys = {
	stepOneUpdate: ["doorStyle"],
	stepOneAdd: ["productLine", "doorStyle"],
	stepTwo: ["shape", "profile", "hinge", "caseColor", "species", "finish", "construction"],
	stepThree: ["distressing", "accentColor", "accentApplication", "packaging"],
	stepFour: ["accountNumber", "billToNumber"]
} as const;

interface AddGlobalAttributesProps {
	isUpdate?: boolean;
	isFixingGlobalAttributes?: boolean;
	showAddItem?: boolean;
	isAddOn?: boolean;
	isLandingPage?: boolean;
	isCustomColor: boolean;
	handleConfigurationUpdate?: (configuration: models["PendingOrderConfigurationViewModel"]) => void;
	setConfigurationToUpdate?: () => void;
	invalidCatalogs?: models["CatalogViewModel"][];
	onGlobalsFixed?: (fixedInvalidCatalogs: models["CatalogViewModel"][]) => void;
	isLoading?: boolean;
	isCustomQuote?: boolean;
	customQuoteGlobals?: models["GlobalsViewModel"];
	isNewOrderQuote?: boolean;
}

const AddGlobalAttributes = ({
	isUpdate,
	isFixingGlobalAttributes,
	showAddItem,
	isAddOn = false,
	isLandingPage,
	isCustomColor,
	handleConfigurationUpdate,
	setConfigurationToUpdate,
	invalidCatalogs,
	onGlobalsFixed,
	isLoading,
	isCustomQuote = false,
	customQuoteGlobals,
	isNewOrderQuote = false
}: AddGlobalAttributesProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [step, setStep] = useState(0);
	const { resetField, trigger, watch, reset } = useFormContext<GlobalAttributeFields>();
	const { openDialog, closeDialog, dialogStatus } = useDialog();
	const { isCustomQuotesLandingPage } = useUserLocation();
	const updatedGlobalAttributes = useSelector((state: RootState) => state.newOrder.updatedGlobalAttributes);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const params = useParams();
	const [getFinishData, { data: finishData }] = useLazyGetFinishDataQuery();
	const [getSpeciesData] = useLazyGetSpeciesDataQuery();
	const draft = useSelector((state: RootState) => state.newOrder.draftDetails?.draftOrder);
	const [currentCatalogIndex, setCurrentCatalogIndex] = useState(0);
	const [fixedCatalogs, setFixedCatalogs] = useState<models["CatalogViewModel"][]>([]);
	const currentCatalog = invalidCatalogs?.[currentCatalogIndex];
	const customQuoteProductLine = customQuoteGlobals?.productLine;
	const steps = useMemo(() => {
		const baseSteps = [
			{
				title: "1 Step",
				description: "1 Step",
				content: (
					<StepOne
						isUpdate={isUpdate}
						isCustomColor={isCustomColor}
						isCustomQuote={isCustomQuote}
						customQuoteGlobals={customQuoteGlobals}
					/>
				)
			},
			{
				title: "2 Step",
				description: "2 Step",
				content: (
					<StepTwo
						isUpdate={isUpdate}
						isCustomColor={isCustomColor}
						isFixingGlobalAttributes={isFixingGlobalAttributes}
						customQuoteGlobals={customQuoteGlobals}
					/>
				)
			},
			{
				title: "3 Step",
				description: "3 Step",
				content: (
					<StepThree
						isUpdate={isUpdate}
						customQuoteGlobals={customQuoteGlobals}
					/>
				)
			}
		];

		if (isFixingGlobalAttributes) {
			return [
				...baseSteps,
				{
					title: "4 Step",
					description: "4 Step",
					content: <StepFive />
				}
			];
		} else {
			if (customQuoteGlobals) return baseSteps;
			return [
				...baseSteps,
				{
					title: "4 Step",
					description: "4 Step",
					content: (
						<StepFour
							isAddOn={isAddOn}
							isUpdate={isUpdate}
							customQuoteProductLine={customQuoteProductLine}
						/>
					)
				}
			];
		}
	}, [
		isUpdate,
		isCustomColor,
		isCustomQuote,
		customQuoteProductLine,
		isFixingGlobalAttributes,
		isAddOn,
		customQuoteGlobals
	]);

	const maxSteps = steps.length;
	const currentStep = steps[step];

	const selectedProductLine = watch("productLine");
	const selectedDoorStyle = watch("doorStyle");
	const selectedSpecies = watch("species");
	const selectedFinish = watch("finish");
	const isFinishDataEmpty =
		finishData?.distressings?.length === 0 &&
		finishData?.accentColors?.length === 0 &&
		finishData?.accentApplications?.length === 0 &&
		finishData?.packagings?.length === 0;

	let pageProps: {
		pathStub: string;
		buttonVariant: "contained" | "outlined";
		buttonWidth: "100%" | "max-content";
	} = {
		pathStub: isAddOn ? "add-ons" : "new-order",
		buttonVariant: isAddOn ? "contained" : "outlined",
		buttonWidth: isAddOn ? "100%" : "max-content"
	};

	useEffect(() => {
		if (selectedProductLine) {
			const fields = [...stepKeys.stepTwo, ...stepKeys.stepThree, ...stepKeys.stepFour] as const;

			resetField("doorStyle", { defaultValue: null as any });

			fields.forEach((field) => resetField(field, { defaultValue: undefined as any })); // null causes some weird validation errors
		}
	}, [dispatch, resetField, selectedProductLine]);

	useEffect(() => {
		if (selectedDoorStyle) {
			const fields = [...stepKeys.stepTwo, ...stepKeys.stepThree] as const;
			fields.forEach((field) => resetField(field, { defaultValue: undefined as any }));
		}
	}, [resetField, selectedDoorStyle]);

	useEffect(() => {
		if (selectedFinish) {
			if (selectedFinish.id && selectedSpecies?.id && selectedDoorStyle?.id && selectedProductLine?.id) {
				getFinishData({
					productLineId: selectedProductLine.id,
					styleId: selectedDoorStyle.id,
					speciesId: selectedSpecies.id,
					finishId: selectedFinish.id
				});
			}
			const fields = [...stepKeys.stepThree] as const;
			fields.forEach((field) => resetField(field, { defaultValue: undefined as any }));
		}
	}, [resetField, selectedFinish, getFinishData, selectedSpecies, selectedDoorStyle, selectedProductLine]);

	useEffect(() => {
		if (selectedSpecies && selectedSpecies.id && selectedDoorStyle?.id && selectedProductLine?.id) {
			getSpeciesData({
				productLineId: selectedProductLine.id,
				styleId: selectedDoorStyle.id,
				speciesId: selectedSpecies.id,
				customFinishes: isCustomColor
			});
		}
	}, [selectedSpecies, selectedDoorStyle, getSpeciesData, isCustomColor, selectedProductLine]);

	useEffect(() => {
		if (customQuoteProductLine) {
			reset({ productLine: customQuoteProductLine });
		}
	}, [customQuoteProductLine, reset]);

	const onStepperForward = async (event: MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();

		let isValid = true;

		if (step === 0) {
			isValid = await trigger(isUpdate ? stepKeys.stepOneUpdate : stepKeys.stepOneAdd);
		} else if (step === 1) {
			isValid = await trigger(stepKeys.stepTwo);
		} else if (step === 2) {
			isValid = await trigger(stepKeys.stepThree);
		}

		if (isValid) {
			if (!isFixingGlobalAttributes) {
				if (step === 1 && isFinishDataEmpty) {
					setStep((prevActiveStep) => prevActiveStep + (isCustomQuote ? 1 : 2));
				} else {
					setStep((prevActiveStep) => prevActiveStep + 1);
				}
			} else {
				setStep((prevActiveStep) => prevActiveStep + 1);
			}
		}
	};

	const onStepperBack = (event: MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		if (step === 3 && isFinishDataEmpty) {
			setStep((prevActiveStep) => prevActiveStep - 2);
		} else {
			setStep((prevActiveStep) => prevActiveStep - 1);
		}
	};

	const handleAddAttributes = () => {
		reset();
		openDialog({
			dialogStatus: isCustomColor ? DialogTracking.CUSTOM_COLOR_ENTRY : DialogTracking.MANUAL_ORDER_ENTRY
		});
		setIsOpen(true);
	};

	const closeAndClear = () => {
		reset();
		setStep(0);
		closeDialog();
		setIsOpen(false);
	};

	const startUpdateAttributes = () => {
		reset();
		if (setConfigurationToUpdate) {
			setConfigurationToUpdate();
		}
		openDialog({
			dialogStatus: isCustomColor ? DialogTracking.CUSTOM_COLOR_ENTRY : DialogTracking.MANUAL_ORDER_ENTRY
		});
		setIsOpen(true);
	};

	const buildNewConfiguration = (values: GlobalAttributeFields): models["PendingOrderConfigurationViewModel"] => {
		return {
			globals: {
				construction: {
					code: values.construction?.code,
					description: values.construction?.description,
					id: values.construction?.id
				},
				finish: {
					code: values.finish?.code,
					description: values.finish?.description,
					id: values.finish?.id,
					customColorSampleOrderNumber: values.finish?.customColorSampleOrderNumber,
					customColor: values.finish?.customColor
				},
				packaging: {
					code: values.packaging?.code,
					description: values.packaging?.description,
					id: values.packaging?.id
				},
				shape: {
					code: values.shape?.code,
					description: values.shape?.description,
					id: values.shape?.id
				},
				species: {
					code: values.species?.code,
					description: values.species?.description,
					id: values.species?.id
				},
				hingeType: {
					code: values.hinge?.code,
					description: values.hinge?.description,
					id: values.hinge?.id
				},
				profile: {
					code: values.profile?.code,
					description: values.profile?.description,
					id: values.profile?.id
				},
				case: {
					code: values.caseColor?.code,
					description: values.caseColor?.description,
					id: values.caseColor?.id
				},
				distressing: {
					code: values.distressing?.code,
					description:
						values.distressing?.description !== NAOptionText ? values.distressing?.description : null,
					id: values.distressing?.id
				},
				accentColor: {
					code: values.accentColor?.code,
					description:
						values.accentColor?.description !== NAOptionText ? values.accentColor?.description : null,
					id: values.accentColor?.id
				},
				accentApplication: {
					code: values.accentApplication?.code,
					description:
						values.accentApplication?.description !== NAOptionText
							? values.accentApplication?.description
							: null,
					id: values.accentApplication?.id
				},
				style: {
					code: values.doorStyle?.code,
					description: values.doorStyle?.description,
					id: values.doorStyle?.id
				},
				productLine: {
					code: values.productLine?.code,
					description: values.productLine?.description,
					id: values.productLine?.id
				},
				brand: {
					code: values.productLine?.parentBrand
				}
			},
			configurationId: uuidv4(),
			accountId: values.accountNumber?.accountId,
			accountName: values.accountNumber?.accountName,
			accountNumber: values.accountNumber?.accountNumber,
			billToId: values.billToNumber?.billToId ?? null,
			lineItems: []
		};
	};

	const handleFixCatalog = (newConfiguration: ReturnType<typeof buildNewConfiguration>) => {
		const updatedCatalog = {
			...currentCatalog,
			globals: newConfiguration.globals,
			isValid: true
		};
		const updatedFixedCatalogs = [...fixedCatalogs, updatedCatalog];
		const isLastCatalog = currentCatalogIndex >= (invalidCatalogs?.length ?? 1) - 1;

		if (!isLastCatalog) {
			setFixedCatalogs(updatedFixedCatalogs);
			setCurrentCatalogIndex(currentCatalogIndex + 1);
			reset();
			setStep(0);
		} else if (onGlobalsFixed) {
			onGlobalsFixed(updatedFixedCatalogs);
		}
	};

	const handleNavigation = (values: GlobalAttributeFields) => {
		if (isAddOn) {
			dispatch(newOrderActions.overwriteDraftOrder({ ...draft, originalOrderId: params.orderId }));
			dispatch(newOrderActions.updateOrderType(OrderType.ADD_ON));
			navigate(`/add-ons/build-order/${params.orderId}`);
		} else if (values.productLine?.customColorEligible && dialogStatus === DialogTracking.CUSTOM_COLOR_ENTRY) {
			dispatch(newOrderActions.updateOrderType(OrderType.CUSTOM_COLOR));
			navigate("/custom-color/build-order");
		} else if (isNewOrderQuote) {
			dispatch(newOrderActions.updateOrderType(OrderType.QUOTE_ORDER));
			navigate("/new-quote/build-order");
		} else if (showAddItem) {
			dispatch(newOrderActions.updateOrderType(OrderType.ORIGINAL));
			navigate("/new-order/build-order");
		} else if (isCustomQuotesLandingPage) {
			navigate("/custom-quote/create-quote");
		}
	};

	const onSave = (values: GlobalAttributeFields) => {
		if (!isUpdate) {
			const newConfiguration = buildNewConfiguration(values);
			if (isFixingGlobalAttributes) {
				handleFixCatalog(newConfiguration);
				return;
			}
			dispatch(newOrderActions.addSingleConfigToOrder(newConfiguration));
			handleNavigation(values);
		} else if (handleConfigurationUpdate && updatedGlobalAttributes && !customQuoteGlobals) {
			const updatedGlobals = {
				...updatedGlobalAttributes.globals,
				...values,
				finish: {
					...updatedGlobalAttributes.globals?.finish,
					...values.finish,
					customColor: values.finish?.customColor
				}
			};

			dispatch(
				newOrderActions.updateGlobalAttributes({
					...updatedGlobalAttributes,
					globals: updatedGlobals
				})
			);
			handleConfigurationUpdate({
				...updatedGlobalAttributes,
				globals: updatedGlobals
			});
		} else if (customQuoteGlobals && handleConfigurationUpdate) {
			const updatedGlobals = {
				...customQuoteGlobals,
				shape: values.shape,
				profile: values.profile,
				hingeType: values.hinge,
				case: values.caseColor,
				species: values.species,
				finish: {
					...customQuoteGlobals?.finish,
					...values.finish
				},
				distressing: values.distressing,
				accentColor: values.accentColor,
				accentApplication: values.accentApplication,
				construction: values.construction,
				packaging: values.packaging,
				style: values.doorStyle,
				productLine: values.productLine
			};
			handleConfigurationUpdate({
				globals: updatedGlobals
			});
		}
		closeAndClear();
	};

	return (
		<>
			{!isFixingGlobalAttributes &&
				(isUpdate ? (
					<Button
						variant="outlined"
						data-testid="update-global-attributes"
						onClick={startUpdateAttributes}
						startIcon={<EditIcon />}
						sx={{ alignItems: "center" }}
						data-id={`${pageProps.pathStub}-build-order-edit-global-attributes-button`}
					>
						{NewOrderUpdateGroup}
					</Button>
				) : (
					<>
						{showAddItem ? (
							<Button
								onClick={handleAddAttributes}
								variant={pageProps.buttonVariant}
								data-id={`${pageProps.pathStub}-landing-page-add-item-button`}
								startIcon={
									<AddIcon
										fontSize="small"
										sx={{ marginBottom: "2px" }}
									/>
								}
								sx={{
									display: "flex",
									alignItems: "center",
									height: "fit-content",
									width: pageProps.buttonWidth,
									marginLeft: "auto"
								}}
							>
								{AddItemText}
							</Button>
						) : (
							<>
								{!isCustomQuote && (
									<AddGlobalsButton
										variant="text"
										onClick={handleAddAttributes}
										data-testid="add-global-attribute-button"
										data-id={`${pageProps.pathStub}-build-order-add-global-attributes-button`}
									>
										<AddIcon />
										{AddGlobalAttributesGroupButton}
									</AddGlobalsButton>
								)}
							</>
						)}
					</>
				))}
			{isFixingGlobalAttributes ? (
				<FixGlobalAttributesDialog
					dialogState={true}
					handleDialogClose={closeAndClear}
					handleStepperBack={onStepperBack}
					handleStepperForward={onStepperForward}
					step={step}
					maxSteps={maxSteps}
					onSave={onSave}
					currentCatalogName={currentCatalog?.name ?? ""}
					currentCatalog={currentCatalogIndex + 1}
					catalogsCount={invalidCatalogs?.length ?? 0}
					csvFileName={currentCatalog?.[fileNameSymbol] ?? ""}
					isLoading={isLoading}
				>
					<DialogContent>{currentStep?.content}</DialogContent>
				</FixGlobalAttributesDialog>
			) : (
				<AddGlobalAttributesDialog
					isUpdate={isUpdate ?? false}
					dialogState={
						isLandingPage
							? dialogStatus === DialogTracking.MANUAL_ORDER_ENTRY ||
								dialogStatus === DialogTracking.CUSTOM_COLOR_ENTRY ||
								dialogStatus === DialogTracking.CUSTOM_QUOTE_ADD_GLOBALS
							: isOpen
					}
					handleDialogClose={closeAndClear}
					handleStepperBack={onStepperBack}
					handleStepperForward={onStepperForward}
					step={step}
					maxSteps={maxSteps}
					onSave={onSave}
				>
					<DialogContent>{currentStep?.content}</DialogContent>
				</AddGlobalAttributesDialog>
			)}
		</>
	);
};

export default AddGlobalAttributes;
